import { useState, useEffect } from 'react';

/**
 * Data Connection Component
 * 
 * Snowflake connection with key-pair or password authentication
 * 
 * @author SureCost Development Team
 * @version 4.0.0
 */
const DataConnection = () => {
    const [connectionConfig, setConnectionConfig] = useState({
        account: '',
        username: '',
        warehouse: '',
        database: '',
        schema: 'PUBLIC',
        role: '',
        useKeyPair: true,
        privateKey: '',
        password: ''
    });
    
    const [isConnected, setIsConnected] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [connectionStatus, setConnectionStatus] = useState('inactive');
    const [lastTested, setLastTested] = useState(null);
    const [showDisconnectModal, setShowDisconnectModal] = useState(false);
    const [connectionError, setConnectionError] = useState('');
    const [hasStoredKey, setHasStoredKey] = useState(false);
    const [isChangingKey, setIsChangingKey] = useState(false);

    useEffect(() => {
        loadConnectionConfig();
    }, []);

    const loadConnectionConfig = async () => {
        try {
            const response = await fetch('/api/admin/data-connection');
            const data = await response.json();
            
            if (data.success) {
                setConnectionConfig({
                    account: data.config.account || '',
                    username: data.config.username || '',
                    warehouse: data.config.warehouse || '',
                    database: data.config.database || '',
                    schema: data.config.schema || 'PUBLIC',
                    role: data.config.role || '',
                    useKeyPair: data.config.useKeyPair !== false,
                    privateKey: '', // Never load the actual key - security best practice
                    password: ''
                });
                
                // Track if a key is stored (without exposing it)
                setHasStoredKey(data.config.hasPrivateKey || false);
                setIsChangingKey(false);
                
                if (data.status === 'active' || data.isConnected) {
                    setConnectionStatus('connected');
                    setIsConnected(true);
                } else {
                    setConnectionStatus('inactive');
                    setIsConnected(false);
                }
                
                if (data.lastTested) {
                    setLastTested(new Date(data.lastTested));
                }
            }
        } catch (error) {
            console.error('Failed to load connection config:', error);
            setConnectionStatus('inactive');
            setIsConnected(false);
        }
    };

    const handleInputChange = (field, value) => {
        setConnectionConfig(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const testConnection = async () => {
        setIsLoading(true);
        setConnectionError('');
        try {
            const response = await fetch('/api/admin/data-connection/test', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                },
                body: JSON.stringify(connectionConfig)
            });

            const result = await response.json();
            
            if (result.success) {
                setConnectionStatus('connected');
                setIsConnected(true);
                setLastTested(new Date());
            } else {
                setConnectionStatus('failed');
                setIsConnected(false);
                setConnectionError(result.message || 'Connection failed');
            }
        } catch (error) {
            console.error('Connection test failed:', error);
            setConnectionStatus('failed');
            setIsConnected(false);
            setConnectionError('Network error - check if backend is running');
        } finally {
            setIsLoading(false);
        }
    };

    const saveConnection = async () => {
        setIsLoading(true);
        try {
            const response = await fetch('/api/admin/data-connection', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                },
                body: JSON.stringify(connectionConfig)
            });

            const result = await response.json();
            if (result.success) {
                loadConnectionConfig();
            }
        } catch (error) {
            console.error('Save failed:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const confirmDisconnect = async () => {
        setShowDisconnectModal(false);
        setIsLoading(true);
        
        try {
            const response = await fetch('/api/admin/data-connection/disconnect', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                }
            });

            const result = await response.json();
            if (result.success) {
                setConnectionStatus('inactive');
                setIsConnected(false);
                setLastTested(null);
            }
        } catch (error) {
            console.error('Disconnect failed:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const statusColors = {
        connected: { bg: '#dcfce7', text: '#166534', dot: '#16a34a' },
        failed: { bg: '#fee2e2', text: '#991b1b', dot: '#dc2626' },
        inactive: { bg: '#f3f4f6', text: '#6b7280', dot: '#9ca3af' }
    };

    const status = statusColors[connectionStatus] || statusColors.inactive;

    return (
        <div style={{ maxWidth: '48rem', margin: '0 auto' }}>
            {/* Header */}
            <div style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                    <h2 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#111827', margin: '0 0 0.25rem 0' }}>
                        Snowflake Connection
                    </h2>
                    <p style={{ fontSize: '0.875rem', color: '#6b7280', margin: '0' }}>
                        Configure your data warehouse connection
                    </p>
                </div>
                
                <div style={{ 
                    padding: '0.5rem 1rem', 
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    backgroundColor: status.bg,
                    color: status.text
                }}>
                    <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: status.dot }} />
                    {connectionStatus === 'connected' ? 'Connected' : connectionStatus === 'failed' ? 'Failed' : 'Not Connected'}
                </div>
            </div>
            
            {/* Error Message */}
            {connectionError && (
                <div style={{
                    marginBottom: '1rem',
                    padding: '0.75rem',
                    backgroundColor: '#fee2e2',
                    border: '1px solid #fecaca',
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem',
                    color: '#991b1b'
                }}>
                    ⚠️ {connectionError}
                </div>
            )}

            {/* Connection Form */}
            <div style={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '0.5rem', boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)' }}>
                <div style={{ padding: '1.5rem' }}>
                    {/* Account */}
                    <div style={{ marginBottom: '1rem' }}>
                        <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.25rem' }}>
                            Account Locator
                        </label>
                        <input
                            type="text"
                            value={connectionConfig.account}
                            onChange={(e) => handleInputChange('account', e.target.value)}
                            placeholder="ABC12345"
                            style={{
                                width: '100%',
                                padding: '0.5rem 0.75rem',
                                fontSize: '0.875rem',
                                border: '1px solid #d1d5db',
                                borderRadius: '0.375rem',
                                boxSizing: 'border-box'
                            }}
                        />
                        <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '0.25rem 0 0 0' }}>
                            Just the account name (e.g., <code style={{ background: '#f3f4f6', padding: '0 4px', borderRadius: '3px' }}>ABC12345</code>) — find it in your Snowflake URL before <code style={{ background: '#f3f4f6', padding: '0 4px', borderRadius: '3px' }}>.snowflakecomputing.com</code>
                        </p>
                    </div>

                    {/* Username & Database */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                        <div>
                            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.25rem' }}>
                                Username
                            </label>
                            <input
                                type="text"
                                value={connectionConfig.username}
                                onChange={(e) => handleInputChange('username', e.target.value)}
                                placeholder="SERVICE_USER"
                                style={{
                                    width: '100%',
                                    padding: '0.5rem 0.75rem',
                                    fontSize: '0.875rem',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '0.375rem',
                                    boxSizing: 'border-box'
                                }}
                            />
                        </div>
                        <div>
                            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.25rem' }}>
                                Database
                            </label>
                            <input
                                type="text"
                                value={connectionConfig.database}
                                onChange={(e) => handleInputChange('database', e.target.value)}
                                placeholder="MY_DATABASE"
                                style={{
                                    width: '100%',
                                    padding: '0.5rem 0.75rem',
                                    fontSize: '0.875rem',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '0.375rem',
                                    boxSizing: 'border-box'
                                }}
                            />
                        </div>
                    </div>

                    {/* Warehouse & Role */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                        <div>
                            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.25rem' }}>
                                Warehouse
                            </label>
                            <input
                                type="text"
                                value={connectionConfig.warehouse}
                                onChange={(e) => handleInputChange('warehouse', e.target.value)}
                                placeholder="COMPUTE_WH"
                                style={{
                                    width: '100%',
                                    padding: '0.5rem 0.75rem',
                                    fontSize: '0.875rem',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '0.375rem',
                                    boxSizing: 'border-box'
                                }}
                            />
                        </div>
                        <div>
                            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.25rem' }}>
                                Role
                            </label>
                            <input
                                type="text"
                                value={connectionConfig.role}
                                onChange={(e) => handleInputChange('role', e.target.value)}
                                placeholder="ACCOUNTADMIN"
                                style={{
                                    width: '100%',
                                    padding: '0.5rem 0.75rem',
                                    fontSize: '0.875rem',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '0.375rem',
                                    boxSizing: 'border-box'
                                }}
                            />
                        </div>
                    </div>

                    {/* Schema */}
                    <div>
                        <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.25rem' }}>
                            Schema
                        </label>
                        <input
                            type="text"
                            value={connectionConfig.schema}
                            onChange={(e) => handleInputChange('schema', e.target.value)}
                            placeholder="PUBLIC"
                            style={{
                                width: '100%',
                                padding: '0.5rem 0.75rem',
                                fontSize: '0.875rem',
                                border: '1px solid #d1d5db',
                                borderRadius: '0.375rem',
                                boxSizing: 'border-box'
                            }}
                        />
                    </div>
                </div>

                {/* Authentication Section */}
                <div style={{ borderTop: '1px solid #e5e7eb', backgroundColor: '#f9fafb', padding: '1.5rem' }}>
                    <h3 style={{ fontSize: '0.875rem', fontWeight: '600', color: '#111827', margin: '0 0 1rem 0' }}>
                        Authentication
                    </h3>
                    
                    {/* Toggle */}
                    <div style={{ 
                        display: 'inline-flex', 
                        backgroundColor: '#e5e7eb',
                        padding: '0.25rem',
                        borderRadius: '0.5rem',
                        marginBottom: '1rem'
                    }}>
                        <button
                            onClick={() => handleInputChange('useKeyPair', true)}
                            style={{
                                padding: '0.5rem 1rem',
                                fontSize: '0.875rem',
                                fontWeight: '500',
                                borderRadius: '0.375rem',
                                border: 'none',
                                cursor: 'pointer',
                                backgroundColor: connectionConfig.useKeyPair ? 'white' : 'transparent',
                                color: connectionConfig.useKeyPair ? '#111827' : '#6b7280',
                                boxShadow: connectionConfig.useKeyPair ? '0 1px 2px rgba(0,0,0,0.05)' : 'none'
                            }}
                        >
                            🔑 Key Pair (.p8 file)
                        </button>
                        <button
                            onClick={() => handleInputChange('useKeyPair', false)}
                            style={{
                                padding: '0.5rem 1rem',
                                fontSize: '0.875rem',
                                fontWeight: '500',
                                borderRadius: '0.375rem',
                                border: 'none',
                                cursor: 'pointer',
                                backgroundColor: !connectionConfig.useKeyPair ? 'white' : 'transparent',
                                color: !connectionConfig.useKeyPair ? '#111827' : '#6b7280',
                                boxShadow: !connectionConfig.useKeyPair ? '0 1px 2px rgba(0,0,0,0.05)' : 'none'
                            }}
                        >
                            🔒 Password
                        </button>
                    </div>
                    
                    {connectionConfig.useKeyPair ? (
                        <div>
                            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
                                Private Key
                            </label>
                            {/* Show stored key indicator OR input field */}
                            {hasStoredKey && !isChangingKey ? (
                                <div>
                                    <div style={{ 
                                        padding: '1rem',
                                        backgroundColor: '#f0fdf4',
                                        border: '1px solid #86efac',
                                        borderRadius: '0.5rem',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between'
                                    }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                            <div style={{
                                                width: '2.5rem',
                                                height: '2.5rem',
                                                backgroundColor: '#22c55e',
                                                borderRadius: '50%',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}>
                                                <svg width="20" height="20" fill="white" viewBox="0 0 24 24">
                                                    <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z"/>
                                                </svg>
                                            </div>
                                            <div>
                                                <div style={{ fontWeight: '600', color: '#166534' }}>Private Key Configured</div>
                                                <div style={{ fontSize: '0.75rem', color: '#15803d' }}>
                                                    Securely stored • Key contents hidden for security
                                                </div>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => setIsChangingKey(true)}
                                            style={{
                                                padding: '0.5rem 1rem',
                                                backgroundColor: 'transparent',
                                                color: '#166534',
                                                border: '1px solid #86efac',
                                                borderRadius: '0.375rem',
                                                fontSize: '0.875rem',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            Change Key
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div>
                                    {isChangingKey && (
                                        <div style={{ 
                                            marginBottom: '0.75rem',
                                            padding: '0.5rem 0.75rem',
                                            backgroundColor: '#fef3c7',
                                            borderRadius: '0.375rem',
                                            fontSize: '0.75rem',
                                            color: '#92400e',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between'
                                        }}>
                                            <span>⚠️ Paste a new key to replace the existing one</span>
                                            <button
                                                onClick={() => {
                                                    setIsChangingKey(false);
                                                    handleInputChange('privateKey', '');
                                                }}
                                                style={{
                                                    background: 'none',
                                                    border: 'none',
                                                    color: '#92400e',
                                                    cursor: 'pointer',
                                                    fontSize: '0.75rem',
                                                    textDecoration: 'underline'
                                                }}
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    )}
                                    <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '0 0 0.5rem 0' }}>
                                        Open your <code style={{ backgroundColor: '#e5e7eb', padding: '0.125rem 0.25rem', borderRadius: '0.25rem' }}>rsa_key.p8</code> file and paste the entire contents below:
                                    </p>
                                    <textarea
                                        value={connectionConfig.privateKey}
                                        onChange={(e) => handleInputChange('privateKey', e.target.value)}
                                        placeholder="-----BEGIN PRIVATE KEY-----
MIIEvQIBADANBgkqhkiG9w0BAQEF...
(paste your entire private key here)
-----END PRIVATE KEY-----"
                                        rows={6}
                                        style={{
                                            width: '100%',
                                            padding: '0.75rem',
                                            fontSize: '0.75rem',
                                            fontFamily: 'monospace',
                                            border: '1px solid #d1d5db',
                                            borderRadius: '0.375rem',
                                            boxSizing: 'border-box',
                                            resize: 'vertical',
                                            backgroundColor: '#fafafa'
                                        }}
                                    />
                                    {connectionConfig.privateKey && (
                                        <div style={{ 
                                            marginTop: '0.5rem',
                                            padding: '0.5rem 0.75rem',
                                            backgroundColor: '#dcfce7',
                                            borderRadius: '0.375rem',
                                            fontSize: '0.75rem',
                                            color: '#166534',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.5rem'
                                        }}>
                                            ✓ New key ready ({connectionConfig.privateKey.length} characters)
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    ) : (
                        <div>
                            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.25rem' }}>
                                Password
                            </label>
                            <input
                                type="password"
                                value={connectionConfig.password}
                                onChange={(e) => handleInputChange('password', e.target.value)}
                                placeholder="Enter your Snowflake password"
                                style={{
                                    width: '100%',
                                    padding: '0.5rem 0.75rem',
                                    fontSize: '0.875rem',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '0.375rem',
                                    boxSizing: 'border-box'
                                }}
                            />
                        </div>
                    )}
                </div>

                {/* Actions */}
                <div style={{ 
                    borderTop: '1px solid #e5e7eb', 
                    padding: '1rem 1.5rem', 
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                        {connectionStatus === 'connected' && lastTested && (
                            <span style={{ color: '#16a34a' }}>✓ Connected at {lastTested.toLocaleString()}</span>
                        )}
                    </div>
                    
                    <div style={{ display: 'flex', gap: '0.75rem' }}>
                        {connectionStatus === 'connected' ? (
                            <button
                                onClick={() => setShowDisconnectModal(true)}
                                disabled={isLoading}
                                style={{
                                    padding: '0.5rem 1rem',
                                    backgroundColor: '#dc2626',
                                    color: 'white',
                                    fontSize: '0.875rem',
                                    fontWeight: '500',
                                    borderRadius: '0.375rem',
                                    border: 'none',
                                    cursor: 'pointer'
                                }}
                            >
                                Disconnect
                            </button>
                        ) : (
                            <button
                                onClick={testConnection}
                                disabled={isLoading || !connectionConfig.account || !connectionConfig.username}
                                style={{
                                    padding: '0.5rem 1rem',
                                    backgroundColor: (!connectionConfig.account || !connectionConfig.username) ? '#d1d5db' : '#2563eb',
                                    color: 'white',
                                    fontSize: '0.875rem',
                                    fontWeight: '500',
                                    borderRadius: '0.375rem',
                                    border: 'none',
                                    cursor: (!connectionConfig.account || !connectionConfig.username) ? 'not-allowed' : 'pointer'
                                }}
                            >
                                {isLoading ? 'Testing...' : 'Test Connection'}
                            </button>
                        )}
                        
                        <button
                            onClick={saveConnection}
                            disabled={isLoading || !connectionConfig.account || !connectionConfig.username}
                            style={{
                                padding: '0.5rem 1rem',
                                backgroundColor: (!connectionConfig.account || !connectionConfig.username) ? '#d1d5db' : '#16a34a',
                                color: 'white',
                                fontSize: '0.875rem',
                                fontWeight: '500',
                                borderRadius: '0.375rem',
                                border: 'none',
                                cursor: (!connectionConfig.account || !connectionConfig.username) ? 'not-allowed' : 'pointer'
                            }}
                        >
                            {isLoading ? 'Saving...' : 'Save Configuration'}
                        </button>
                    </div>
                </div>
            </div>
            
            {/* Disconnect Modal */}
            {showDisconnectModal && (
                <div style={{
                    position: 'fixed',
                    inset: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        borderRadius: '0.5rem',
                        padding: '1.5rem',
                        maxWidth: '24rem',
                        width: '90%'
                    }}>
                        <h3 style={{ fontSize: '1.125rem', fontWeight: '600', margin: '0 0 0.5rem 0' }}>
                            Disconnect from Snowflake?
                        </h3>
                        <p style={{ fontSize: '0.875rem', color: '#6b7280', margin: '0 0 1rem 0' }}>
                            This will reset your connection status.
                        </p>
                        <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                            <button
                                onClick={() => setShowDisconnectModal(false)}
                                style={{
                                    padding: '0.5rem 1rem',
                                    backgroundColor: 'white',
                                    color: '#374151',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '0.375rem',
                                    cursor: 'pointer'
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmDisconnect}
                                style={{
                                    padding: '0.5rem 1rem',
                                    backgroundColor: '#dc2626',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '0.375rem',
                                    cursor: 'pointer'
                                }}
                            >
                                Disconnect
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DataConnection;
